import { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Form } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faPlus,
  faUser,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import swal from "sweetalert";
import { APISD } from "../../../../../../config/apisd";
import { APISM } from "../../../../../../config/apism";
// import LoaderAction from "../../../../../../Loader/LoaderHome";
import LoaderAction from "../../../../../Loader/LoaderHome";
import ToastSuccess from "../../../../../NotificationToast/ToastSuccess";

const getUserId = () =>
  String(localStorage.getItem("id_admin") || "").replace(
    /[|&;$%@"<>()+,]/g,
    "",
  );

export default function WalasFormModal({
  mode,
  context,
  dataUpdate,
  dataCreate,
  GetResponseData,
  onHide,
  ...modalProps
}) {
  const token = localStorage.getItem("token");
  const requestConfig = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token],
  );
  const [loading, setLoading] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [selectedSemesterIds, setSelectedSemesterIds] = useState([]);
  const [classId, setClassId] = useState("");
  const isUpdate = mode === "update";

  useEffect(() => {
    const semesterIds = (context?.semesterList || [])
      .map((item) => item?.id)
      .filter(Boolean);
    setSelectedSemesterIds(semesterIds);
    setClassId(
      isUpdate
        ? dataUpdate?.ysb_kelas_id || ""
        : dataCreate?.ysb_kelas_id || context?.classes?.[0]?.ysb_kelas_id || "",
    );
    setSelectedTeacherIds(
      isUpdate
        ? (dataUpdate?.teachers || [])
            .map((item) => item?.ysb_teacher_id)
            .filter(Boolean)
        : [],
    );
  }, [context, dataCreate, dataUpdate, isUpdate]);

  useEffect(() => {
    const fetchTeachers = async () => {
      if (!context?.schoolId) return;
      setLoadingTeachers(true);
      try {
        const response = await APISM.get(
          `/api/access/teachers?page=1&limit=1000&ascending=1&search=&branch=${context.branchId}`,
          requestConfig,
        );
        const result = response?.data?.data || [];
        setTeachers(
          result.filter(
            (item) =>
              !item?.ysb_school_id || item?.ysb_school_id === context.schoolId,
          ),
        );
      } catch (error) {
        setTeachers([]);
        swal({
          title: "Gagal",
          text:
            error?.response?.data?.message ||
            "Gagal mengambil daftar wali kelas",
          icon: "error",
        });
      } finally {
        setLoadingTeachers(false);
      }
    };
    fetchTeachers();
  }, [context?.branchId, context?.schoolId, requestConfig]);

  // Pertahankan urutan guru sesuai urutan saat dipilih, bukan urutan daftar guru.
  const selectedTeachers = useMemo(() => {
    const teacherById = new Map(
      teachers.map((teacher) => [String(teacher?.id), teacher]),
    );
    return selectedTeacherIds
      .map((id) => teacherById.get(String(id)))
      .filter(Boolean);
  }, [selectedTeacherIds, teachers]);
  const displayedTeachers = useMemo(() => {
    const keyword = teacherSearch.trim().toLowerCase();
    return teachers.filter(
      (item) =>
        !keyword ||
        [item?.full_name, item?.nik_ysb, item?.nip_ypi].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(keyword),
        ),
    );
  }, [teacherSearch, teachers]);
  const selectedClass =
    (context?.classes || []).find(
      (item) => String(item?.ysb_kelas_id) === String(classId),
    ) ||
    dataUpdate;

  const toggleSemester = (semesterId) =>
    setSelectedSemesterIds((current) =>
      current.includes(semesterId)
        ? current.filter((id) => id !== semesterId)
        : [...current, semesterId],
    );
  const toggleTeacher = (teacherId) =>
    setSelectedTeacherIds((current) =>
      current.includes(teacherId)
        ? current.filter((id) => id !== teacherId)
        : [...current, teacherId],
    );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      !classId ||
      selectedSemesterIds.length === 0 ||
      selectedTeacherIds.length === 0
    )
      return;
    setLoading(true);
    try {
      const endpoint = isUpdate
        ? "/api/walas-assignments/update"
        : "/api/walas-assignments/store";
      const method = isUpdate ? "put" : "post";
      await Promise.all(
        selectedSemesterIds.map((semesterId) => {
          const semester = context.semesterList.find(
            (item) => item?.id === semesterId,
          );
          return APISD[method](
            endpoint,
            {
              ysb_academic_year_id:
                semester?.ysb_academic_year_id ||
                semester?.academic_year_id ||
                context?.academicYearId,
              ysb_semester_id: semesterId,
              ysb_branch_id: context.branchId,
              ysb_school_id: context.schoolId,
              ysb_grade_level_id:
                selectedClass?.ysb_grade_level_id ||
                dataUpdate?.ysb_grade_level_id ||
                dataCreate?.ysb_grade_level_id,
              ysb_kelas_id: classId,
              ysb_teacher_id: selectedTeacherIds,
              user_id: getUserId(),
            },
            requestConfig,
          );
        }),
      );
      ToastSuccess.fire({
        icon: "success",
        title: isUpdate
          ? "Wali kelas berhasil diperbarui"
          : "Wali kelas berhasil ditambahkan",
      });
      await GetResponseData?.();
      onHide();
    } catch (error) {
      swal({
        title: "Gagal",
        text:
          error?.response?.data?.message ||
          `Gagal ${isUpdate ? "memperbarui" : "menambahkan"} wali kelas`,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

  const toTitleCaseGelar = (value) => {
    const s = safeStr(value).toLowerCase().trim();
    if (!s) return "";

    return s
      .split(" ")
      .map((word) => {
        // khusus gelar yang ada titik, contoh: s.si. -> S.Si.
        if (word.includes(".")) {
          return word
            .split(".")
            .map((part) => {
              if (!part) return "";
              return part.charAt(0).toUpperCase() + part.slice(1);
            })
            .join(".");
        }

        // normal title case, contoh: helmy -> Helmy
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  return (
    <>
      {loading && <LoaderAction />}
      <Modal
        {...modalProps}
        show
        size="lg"
        centered
        backdrop="static"
        keyboard={!loading}
        dialogClassName="walas-modal-dialog"
        contentClassName="walas-modal-content"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header className="walas-modal-header">
            <h5>{isUpdate ? "Kelola Wali Kelas" : "Tambah Wali Kelas"}</h5>
            <button
              type="button"
              onClick={onHide}
              disabled={loading}
              aria-label="Tutup"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </Modal.Header>
          <Modal.Body className="walas-modal-body">
            <section className="walas-info-grid">
              <div>
                <small>TAHUN AJARAN</small>
                <strong>{context?.academicYear || "-"}</strong>
              </div>
              <div>
                <small>CABANG</small>
                <strong>{context?.branchName || "-"}</strong>
              </div>
              <div>
                <small>SEKOLAH</small>
                <strong>{context?.schoolName || "-"}</strong>
              </div>
              {(isUpdate || dataCreate) && (
                <div>
                  <small>KELAS</small>
                  <strong>
                    {dataUpdate?.name_kelas || dataCreate?.name_kelas || "-"}
                  </strong>
                </div>
              )}
            </section>

            <div className="walas-field">
              <label>
                Pilih Semester <span>*</span>
              </label>
              <div className="walas-semesters">
                {(context?.semesterList || []).map((semester) => (
                  <label key={semester?.id}>
                    <input
                      type="checkbox"
                      checked={selectedSemesterIds.includes(semester?.id)}
                      onChange={() => toggleSemester(semester?.id)}
                    />
                    <span>Semester {semester?.semester}</span>
                  </label>
                ))}
              </div>
            </div>

            {!isUpdate && !dataCreate && (
              <div className="walas-field">
                <label>
                  Pilih Kelas <span>*</span>
                </label>
                <select
                  value={classId}
                  onChange={(event) => setClassId(event.target.value)}
                  required
                >
                  <option value="" disabled>
                    Pilih Kelas
                  </option>
                  {(context?.classes || []).map((item) => (
                    <option key={item?.ysb_kelas_id} value={item?.ysb_kelas_id}>
                      {item?.name_level} - {item?.name_kelas}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <section className="walas-picker">
              <div className="walas-picker-head">
                <div>
                  <h6>Atur Wali Kelas</h6>
                  <p>
                    Cari wali kelas baru atau hapus wali kelas terpilih melalui
                    tanda silang.
                  </p>
                </div>
                <span>{selectedTeacherIds.length} dipilih</span>
              </div>
              {selectedTeachers.length > 0 && (
                <div className="walas-chips">
                  {selectedTeachers.map((teacher) => (
                    <button
                      type="button"
                      key={teacher?.id}
                      onClick={() => toggleTeacher(teacher?.id)}
                    >
                      <FontAwesomeIcon icon={faUser} /> {toTitleCaseGelar(teacher?.full_name)}{" "}
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  ))}
                </div>
              )}
              <div className="walas-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <input
                  value={teacherSearch}
                  onChange={(event) => setTeacherSearch(event.target.value)}
                  placeholder="Cari nama wali kelas atau NIK..."
                />
              </div>
              <div className="walas-teacher-list">
                {loadingTeachers ? (
                  <div className="walas-list-info">
                    Memuat daftar wali kelas...
                  </div>
                ) : displayedTeachers.length === 0 ? (
                  <div className="walas-list-info">
                    Wali kelas tidak ditemukan
                  </div>
                ) : (
                  displayedTeachers.map((teacher) => {
                    const chosen = selectedTeacherIds.includes(teacher?.id);
                    return (
                      <div className="walas-teacher" key={teacher?.id}>
                        <span className="walas-avatar">
                          <FontAwesomeIcon icon={faUser} />
                        </span>
                        <div>
                          <strong>{toTitleCaseGelar(teacher?.full_name) || "-"}</strong>
                          <small>
                            {teacher?.nik_ysb || teacher?.nip_ypi || "-"}
                          </small>
                        </div>
                        <button
                          type="button"
                          className={chosen ? "chosen" : ""}
                          onClick={() => toggleTeacher(teacher?.id)}
                        >
                          {chosen ? (
                            <>
                              <FontAwesomeIcon icon={faXmark} /> Hapus
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faPlus} /> Pilih
                            </>
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </Modal.Body>
          <Modal.Footer className="walas-modal-footer">
            <p>
              <strong>{selectedTeacherIds.length} wali kelas</strong> akan
              diterapkan ke{" "}
              <strong>
                {selectedClass?.name_kelas
                  ? `kelas ${selectedClass.name_kelas}`
                  : "1 kelas"}
              </strong>
              .
            </p>
            <div>
              <button
                type="button"
                className="walas-cancel"
                onClick={onHide}
                disabled={loading}
              >
                Batal
              </button>
              <button
                type="submit"
                className="walas-submit"
                disabled={
                  loading ||
                  !classId ||
                  selectedSemesterIds.length === 0 ||
                  selectedTeacherIds.length === 0
                }
              >
                <FontAwesomeIcon icon={faUsers} />{" "}
                {loading
                  ? "Menyimpan..."
                  : isUpdate
                    ? "Simpan Perubahan"
                    : "Simpan Wali Kelas"}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
      <style>{`
      .walas-modal-dialog{max-width:900px}.walas-modal-content{border:0;border-radius:18px;overflow:hidden;box-shadow:0 24px 70px rgba(30,50,90,.2)}.walas-modal-header{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid #e4e9f3;background:#fff}.walas-modal-header h5{margin:0;font-size:20px;font-weight:800;color:#14213d}.walas-modal-header button{display:grid;place-items:center;width:36px;height:36px;border:0;border-radius:9px;background:#f1f5f9;font-size:18px;color:#64748b}.walas-modal-body{padding:22px 24px;max-height:68vh;overflow-y:auto;background:#f8fafc}.walas-info-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;padding:15px;border:1px solid #d7e3fa;border-radius:12px;background:#f1f6ff;margin-bottom:20px}.walas-info-grid div{display:flex;min-width:0;flex-direction:column;gap:4px}.walas-info-grid small{font-size:10px;font-weight:800;color:#8290a7;letter-spacing:.35px}.walas-info-grid strong{overflow:hidden;color:#334155;font-size:13px;text-overflow:ellipsis;white-space:nowrap}.walas-field{margin-bottom:18px}.walas-field>label{display:block;font-size:13px;font-weight:800;color:#26334d;margin-bottom:8px}.walas-field>label span{color:#ef4444}.walas-semesters{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.walas-semesters label{display:flex;align-items:center;gap:10px;margin:0;border:1px solid #d6e0ef;border-radius:10px;padding:11px 13px;background:#fff;font-size:13px;font-weight:700;cursor:pointer}.walas-semesters label:has(input:checked){border-color:#9db4ff;background:#f0f4ff;color:#2949bd}.walas-semesters input{width:17px;height:17px;accent-color:#4965ec}.walas-field select{width:100%;height:44px;padding:0 13px;border:1px solid #d3ddef;border-radius:10px;background:#fff;font-size:13px}.walas-picker{padding:17px;border:1px solid #dce3ef;border-radius:14px;background:#fff}.walas-picker-head{display:flex;align-items:flex-start;justify-content:space-between;gap:15px}.walas-picker-head h6{margin:0 0 3px;font-size:16px;font-weight:800;color:#1e293b}.walas-picker-head p{margin:0;color:#70819d;font-size:12px}.walas-picker-head>span{height:28px;padding:5px 10px;border-radius:16px;background:#edf2ff;color:#3454ca;font-size:11px;font-weight:800;white-space:nowrap}.walas-chips{display:flex;gap:7px;flex-wrap:wrap;margin:14px 0 10px}.walas-chips button{border:1px solid #c8d7fb;border-radius:18px;background:#eff5ff;color:#3152a4;padding:7px 10px;font-size:12px;font-weight:700}.walas-search{height:42px;display:flex;align-items:center;gap:10px;border:1px solid #ccd7e8;border-radius:10px;padding:0 13px;color:#8ca0bc}.walas-search:focus-within{border-color:#7794ed;box-shadow:0 0 0 3px rgba(69,97,233,.1)}.walas-search input{border:0;outline:0;width:100%;font-size:13px}.walas-teacher-list{max-height:235px;overflow-y:auto;border:1px solid #e2e8f0;border-radius:10px;margin-top:9px}.walas-teacher{display:grid;grid-template-columns:38px minmax(0,1fr) auto;align-items:center;gap:10px;padding:9px 11px;border-bottom:1px solid #e8edf4}.walas-teacher:last-child{border:0}.walas-teacher:hover{background:#f8faff}.walas-avatar{width:36px;height:36px;border-radius:50%;display:grid;place-items:center;background:#dce8ff;color:#4164df}.walas-teacher div{display:flex;min-width:0;flex-direction:column}.walas-teacher strong{overflow:hidden;font-size:13px;color:#111827;text-overflow:ellipsis;white-space:nowrap}.walas-teacher small{font-size:11px;color:#7585a0}.walas-teacher button{border:0;border-radius:8px;background:#edf3ff;color:#3158d4;padding:7px 10px;font-size:12px;font-weight:700}.walas-teacher button.chosen{background:#fff0f1;color:#dc3545}.walas-list-info{text-align:center;padding:28px;color:#7b8aa3;font-size:13px}.walas-modal-footer{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:14px 24px;border-top:1px solid #e4e9f3;background:#fff}.walas-modal-footer p{margin:0;color:#72819a;font-size:12px}.walas-modal-footer p strong{color:#273248}.walas-modal-footer>div{display:flex;gap:8px}.walas-modal-footer button{height:40px;padding:0 15px;border-radius:9px;font-size:13px;font-weight:800}.walas-cancel{border:1px solid #d2dbea;background:#fff;color:#4d5a70}.walas-submit{border:0;background:#4561e9;color:#fff}.walas-submit:disabled{opacity:.55}@media(max-width:760px){.walas-modal-dialog{margin:10px}.walas-modal-body{padding:17px}.walas-info-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.walas-modal-footer{align-items:stretch;flex-direction:column;gap:10px}.walas-modal-footer>div,.walas-modal-footer button{width:100%}.walas-picker-head{flex-direction:column;gap:8px}.walas-teacher{grid-template-columns:34px minmax(0,1fr) auto}.walas-avatar{width:32px;height:32px}}@media(max-width:480px){.walas-info-grid,.walas-semesters{grid-template-columns:1fr}.walas-teacher button{padding:7px 8px}.walas-modal-footer>div{flex-direction:column-reverse}}
    `}</style>
    </>
  );
}
